const NEVER_PREFIXES = [
  '/superadmin/engineers',
  '/superadmin/dashboard',
  '/zahlung',
  '/director/users/superadmins',
]

const TEACHER_EXCLUSIVE = [
  'substitution.request',
  'substitution.accept',
  'substitution.decline',
  'substitution.cancel',
]

const HR_EXCLUSIVE = [
  'hr.report.export',
  'hr.report.schedule',
  'hr.data.edit',
]

const DIRECTOR_EXCLUSIVE = [
  'director.case.adjust',
  'director.decisions.create',
  'director.documents.request',
  'director.manual.assign',
]

const ADMIN_EXCLUSIVE = [
  'substitution.book',
  'substitution.delete.booked',
  'substitution.request.on_behalf',
  'substitution.direct.assign',
]

export const SUPERADMIN_UNIQUE = [
  'school.read',
  'school.update',
  'school.settings.manage',
  'teacher.qualification.manage',
  'timetable.parse',
  'radar.view.all',
  'holiday.read',
  'holiday.create',
  'holiday.update',
  'holiday.delete',
  'holiday.import',
  'parsing.configure',
  'parsing.train',
  'parsing.validate',
  'notification.configure',
  'superadmin.dashboard.view',
  'audit_log.view',
]

const ADMIN_GATED_PREFIXES = [
  '/admin/anfrage-fuer-lehrer',
  '/admin/stellvertretung',
  '/admin/substitutions-log',
  '/admin/substitutions',
  '/admin/auto-re-request',
  '/admin/waiting-pool',
  '/admin/cancellations',
  '/admin/live-status',
  '/admin/conflicts',
  '/admin/admins',
  '/admin/teachers',
  '/admin/timetables',
  '/admin/settings',
  '/admin/school-params',
]

const PATH_ANY: Array<[string, string[]]> = [
  ['/dashboard', []],
  ['/engineer/schools', []],
  ['/messages', []],
  ['/help', []],
  ['/superadmin/users', ['user.read', 'user.list']],
  ['/superadmin/timetable-import', ['timetable.parse']],

  ['/admin/qualifications', ['teacher.qualification.manage']],
  ['/superadmin/settings/teacher-params', ['school.settings.manage', 'teacher.update']],
  ['/admin/school-params', ['teacher.update']],
  ['/superadmin/email-templates', ['notification.configure']],
  ['/superadmin/files', ['school.read']],
  ['/superadmin/patterns', ['parsing.configure']],
  ['/superadmin/audit-log', ['audit_log.view']],
  ['/superadmin/settings', ['school.update', 'school.settings.manage']],
  ['/admin/anfrage-fuer-lehrer', ['substitution.request.on_behalf']],
  ['/admin/stellvertretung', ['substitution.direct.assign']],
  ['/admin/substitutions-log', ['substitution.read']],
  ['/admin/substitutions', ['admin.dashboard.view', 'substitution.manage']],
  ['/admin/auto-re-request', ['substitution.manage']],
  ['/admin/waiting-pool', ['substitution.manage']],
  ['/admin/cancellations', ['substitution.manage']],
  ['/admin/live-status', ['substitution.read']],
  ['/admin/conflicts', ['timetable.read']],
  ['/admin/admins', ['user.list']],
  ['/admin/teachers', ['teacher.read']],
  ['/admin/timetables', ['timetable.read']],
  ['/admin/settings/calendar', ['holiday.read', 'holiday.create', 'holiday.update', 'holiday.delete', 'holiday.import']],
  ['/admin/settings', ['school.read']],
  ['/director/dashboard', ['director.decisions.view']],
  ['/director/decisions', ['director.decisions.view']],
  ['/director/delegations', ['director.manual.assign']],
  ['/director/settings', ['director.decisions.view']],
  ['/hr/pult', ['hr.data.edit', 'hr.statistics.view']],
  ['/hr/tabelle', ['hr.report.view']],
  ['/hr/ipb', ['hr.data.edit']],
  ['/hr/reports', ['hr.report.view']],
  ['/hr/settings', ['hr.data.edit']],
  ['/teacher/dashboard', ['substitution.request', 'timetable.read']],
  ['/teacher/timetable', ['timetable.read']],
  ['/teacher/incoming-requests', ['substitution.accept']],
  ['/teacher/requests', ['substitution.request']],
  ['/teacher/bookings', ['substitution.read']],
  ['/teacher/new-request', ['substitution.request']],
  ['/teacher/request', ['substitution.request']],
  ['/teacher/chats', ['substitution.request']],
  ['/teacher/settings', ['user.update']],
]

function hasAny(scopes: string[], needed: string[]): boolean {
  if (needed.length === 0) return true
  return needed.some((p) => scopes.includes(p))
}

function groupAllowed(path: string, scopes: string[]): boolean {
  if (path.startsWith('/teacher')) {
    return TEACHER_EXCLUSIVE.some((p) => scopes.includes(p))
  }
  if (path.startsWith('/hr')) {
    return HR_EXCLUSIVE.some((p) => scopes.includes(p))
  }
  if (path.startsWith('/director')) {
    return DIRECTOR_EXCLUSIVE.some((p) => scopes.includes(p))
  }
  if (path.startsWith('/superadmin')) {
    return SUPERADMIN_UNIQUE.some((p) => scopes.includes(p))
  }
  if (path === '/admin/settings/calendar' || path.startsWith('/admin/settings/calendar/')) {
    return ['holiday.read', 'holiday.create', 'holiday.update', 'holiday.delete', 'holiday.import'].some((p) => scopes.includes(p))
  }
  if (ADMIN_GATED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) {
    if (
      (path === '/admin/substitutions' || path.startsWith('/admin/substitutions/'))
      && scopes.includes('admin.dashboard.view')
    ) {
      return true
    }
    return ADMIN_EXCLUSIVE.some((p) => scopes.includes(p))
  }
  return true
}

export function engineerCanAccessPath(pathname: string, scopes: string[]): boolean {
  const path = pathname.split('?')[0] ?? pathname
  if (
    path === '/dashboard' ||
    path === '/engineer/schools' || path.startsWith('/engineer/schools/') ||
    path === '/messages' || path.startsWith('/messages/') ||
    path === '/help' || path.startsWith('/help')
  ) {
    return true
  }
  if (NEVER_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) {
    return false
  }
  let match: [string, string[]] | undefined
  for (const entry of PATH_ANY) {
    const prefix = entry[0]
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      if (!match || prefix.length > match[0].length) match = entry
    }
  }
  if (!match) return false
  if (!groupAllowed(match[0], scopes)) return false
  return hasAny(scopes, match[1])
}

export function engineerVisiblePath(path: string, scopes: string[]): boolean {
  return engineerCanAccessPath(path, scopes)
}
